/**
 * useIncidentData.ts — Hook for nself-incident-mgmt plugin (port 3833)
 *
 * Purpose:    Fetch and manage the incident lifecycle for the Incidents dashboard.
 * Inputs:     None (reads from plugin backend port 3833)
 * Outputs:    { state, data, error, refresh, acknowledgeIncident, resolveIncident, createIncident }
 * Constraints: Client-side only; uses pluginFetch; AsyncState for UI
 * SOT:        T-P3-E5-W2-S4-T01
 */

import { useCallback, useEffect, useState } from 'react'
import { pluginFetch } from '../lib/plugin-client'
import type { SentryError } from '../lib/sentry-error'
import type { AsyncState } from '../components/AsyncScreen'

const PORT = 3833

export type IncidentSeverity = 'p1' | 'p2' | 'p3' | 'p4'
export type IncidentStatus = 'open' | 'acknowledged' | 'resolved'

export interface IncidentTimelineEntry {
  id: string
  type: 'opened' | 'acknowledged' | 'comment' | 'resolved'
  author: string
  message: string
  createdAt: string
}

export interface Incident {
  id: string
  title: string
  severity: IncidentSeverity
  status: IncidentStatus
  assignee?: string
  runbookUrl?: string
  postMortemUrl?: string
  openedAt: string
  acknowledgedAt?: string
  resolvedAt?: string
  timeline: IncidentTimelineEntry[]
}

export interface IncidentData {
  incidents: Incident[]
}

export interface CreateIncidentPayload {
  title: string
  severity: IncidentSeverity
  runbookUrl?: string
}

export function useIncidentData() {
  const [state, setState] = useState<AsyncState>('loading')
  const [data, setData] = useState<IncidentData | null>(null)
  const [error, setError] = useState<SentryError | undefined>()

  const load = useCallback(async () => {
    setState('loading')
    setError(undefined)
    if (!navigator.onLine) {
      setState('offline')
      return
    }
    const result = await pluginFetch<IncidentData>(PORT, '/api/incidents')
    if (result.ok) {
      setData(result.value)
      setState(result.value.incidents.length === 0 ? 'empty' : 'success')
    } else {
      const e = result.error
      if (e.type === 'bundle_required') setState('permission-denied')
      else if (e.type === 'rate_limit') setState('rate-limited')
      else if (e.type === 'plugin_offline') setState('offline')
      else setState('error')
      setError(e)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const acknowledgeIncident = useCallback(
    async (id: string): Promise<boolean> => {
      const result = await pluginFetch(PORT, `/api/incidents/${id}/acknowledge`, {
        method: 'POST',
      })
      if (result.ok) { await load() }
      return result.ok
    },
    [load],
  )

  const resolveIncident = useCallback(
    async (id: string, postMortemUrl?: string): Promise<boolean> => {
      const result = await pluginFetch(PORT, `/api/incidents/${id}/resolve`, {
        method: 'POST',
        body: JSON.stringify({ postMortemUrl }),
      })
      if (result.ok) { await load() }
      return result.ok
    },
    [load],
  )

  const createIncident = useCallback(
    async (payload: CreateIncidentPayload): Promise<{ ok: boolean; error?: string }> => {
      const result = await pluginFetch<Incident>(PORT, '/api/incidents', {
        method: 'POST',
        body: JSON.stringify(payload),
      })
      if (result.ok) {
        await load()
        return { ok: true }
      }
      return { ok: false, error: result.error.message }
    },
    [load],
  )

  return { state, data, error, refresh: load, acknowledgeIncident, resolveIncident, createIncident }
}
